/**
 * batch-66 planbord-klantfilter (FO-487) - pure kern van de klantplanning-
 * weergave op het planbord.
 *
 * Met een klantfilter toont het bord alleen de monteurs die in de zichtbare
 * periode bij die klant(en) staan. Alles wat daar omheen moet kloppen staat
 * hier, zonder DB of React, zodat het los te testen is:
 *
 *   - de telling per dag, per week en over de periode (de API rekent hem uit
 *     over de volledige dagregel-selectie, nooit het bord over geladen rijen);
 *   - de splitsing van de rijen in "ingepland" en "niet ingepland" (de
 *     scheidingsregel op het bord);
 *   - de schakelaar "Toon ook niet-ingeplande monteurs": URL plus voorkeur;
 *   - de teksten van de kop, zodat kop, scheidingsregel en drop-zone dezelfde
 *     klantnaam en periode-aanduiding gebruiken.
 */
import type {
  PlanningBoardKlantInzetDag,
  PlanningBoardKlantInzetTelling,
  PlanningBoardKlantInzetWeek,
} from '@streep/types'
import { listDatesInPeriod, type PlanningPeriod, type PlanningView } from './planning-period'
import { isoWekenInPeriode } from './werkbon-facturatie'

/** Het minimum dat de telling van een dagregel nodig heeft. */
export interface KlantInzetDagregel {
  /** `WerkbonDayEntry.date`: UTC-middernacht van de kalenderdag. */
  date: Date
  /** `null` = onbemand ("wordt ingepland"). */
  employeeId: string | null
}

export interface KlantInzetTellingen {
  perDag: PlanningBoardKlantInzetDag[]
  weken: PlanningBoardKlantInzetWeek[]
  totaal: PlanningBoardKlantInzetTelling
}

const dagSleutel = (d: Date): string => d.toISOString().slice(0, 10)

function tel(regels: readonly KlantInzetDagregel[]): PlanningBoardKlantInzetTelling {
  const monteurs = new Set<string>()
  let onbemand = 0
  for (const r of regels) {
    if (r.employeeId) monteurs.add(r.employeeId)
    else onbemand += 1
  }
  return { monteurs: monteurs.size, diensten: regels.length, onbemand }
}

/**
 * De inzet bij de gefilterde klant(en): per dag, per ISO-week en over de hele
 * periode.
 *
 * `monteurs` is overal een DISTINCT-telling: een monteur met twee diensten op
 * een dag is één monteur, en een monteur die vijf dagen staat telt in het
 * weektotaal één keer. Daarom is het weektotaal niet uit de dagregels van de
 * kop op te tellen en moet het hier, bij de ruwe regels, berekend worden.
 * Onbemande diensten zijn niet op naam te ontdubbelen en tellen elk apart.
 *
 * Elke dag van de periode krijgt een regel, ook zonder inzet: het bord tekent
 * de kop per dagkolom en een gat is juist de informatie.
 */
export function telKlantInzet(
  periode: PlanningPeriod,
  dagregels: readonly KlantInzetDagregel[],
): KlantInzetTellingen {
  const dagen = listDatesInPeriod(periode)
  const perDagRegels = new Map<string, KlantInzetDagregel[]>(dagen.map((d) => [d, []]))
  // Alleen regels binnen de periode tellen mee; zo kan een te ruime query de
  // kop nooit laten afwijken van het raster eronder.
  const binnen: KlantInzetDagregel[] = []
  for (const r of dagregels) {
    const lijst = perDagRegels.get(dagSleutel(r.date))
    if (!lijst) continue
    lijst.push(r)
    binnen.push(r)
  }

  const perDag = dagen.map((datum) => ({ datum, ...tel(perDagRegels.get(datum) ?? []) }))

  const eersteDag = dagen[0]
  const laatsteDag = dagen[dagen.length - 1]
  const weken: PlanningBoardKlantInzetWeek[] = []
  if (eersteDag && laatsteDag) {
    for (const week of isoWekenInPeriode(periode.start, periode.end)) {
      // Een maandweergave begint en eindigt midden in een week: knip de week af
      // op de periode, zodat "van/tot" precies de dagen benoemt die meetellen.
      const weekVan = dagSleutel(week.van)
      const weekTot = dagSleutel(week.tot)
      const van = weekVan < eersteDag ? eersteDag : weekVan
      const tot = weekTot > laatsteDag ? laatsteDag : weekTot
      if (van > tot) continue
      const regels = binnen.filter((r) => {
        const dag = dagSleutel(r.date)
        return dag >= van && dag <= tot
      })
      weken.push({ weekNr: week.weekNr, van, tot, ...tel(regels) })
    }
  }

  return { perDag, weken, totaal: tel(binnen) }
}

/**
 * Splitst de bord-rijen in "ingepland bij de klant" en de rest, met behoud van
 * de volgorde binnen elke groep (de sortering die de planner koos blijft dus
 * gelden). Een rij zonder vlag (geen klantfilter, of een oudere API-revisie)
 * telt als ingepland: dan is er niets te scheiden.
 */
export function splitsKlantRijen<T extends { ingeplandBijKlant?: boolean }>(
  rijen: readonly T[],
): { ingepland: T[]; nietIngepland: T[] } {
  const ingepland: T[] = []
  const nietIngepland: T[] = []
  for (const rij of rijen) {
    if (rij.ingeplandBijKlant === false) nietIngepland.push(rij)
    else ingepland.push(rij)
  }
  return { ingepland, nietIngepland }
}

/**
 * De stand van "Toon ook niet-ingeplande monteurs". De URL wint (`1` aan, `0`
 * uit), zodat een gedeelde link en een reload exact dezelfde weergave geven;
 * zonder param geldt de opgeslagen voorkeur van de gebruiker (default uit).
 */
export function resolveToonNietIngepland(
  urlParam: string | null | undefined,
  voorkeurAan: boolean,
): boolean {
  if (urlParam === '1') return true
  if (urlParam === '0') return false
  return voorkeurAan
}

/**
 * Wat hoort er in de URL? `null` = weglaten.
 *
 * Zodra de planner de schakelaar zelf heeft omgezet (of de URL de stand al
 * droeg) is de keuze EXPLICIET en staat hij altijd in de URL, als `1` of `0`.
 * Dan is de URL de enige waarheid en maakt het niet uit of de opgeslagen
 * voorkeur al is bijgewerkt, in een ander tabblad is veranderd of niet kon
 * worden opgeslagen: de schakelaar kan niet terugspringen. Een eerdere versie
 * liet de `0` weg op basis van wat het bord dacht dat de opgeslagen voorkeur
 * was; liep die aanname achter, dan sprong de schakelaar terug.
 *
 * Niet expliciet (verse entry, deeplink zonder param): de URL blijft schoon en
 * de opgeslagen voorkeur beslist. Zonder klantfilter heeft de param geen
 * betekenis.
 */
export function toonNietIngeplandUrlParam(stand: {
  klantfilterActief: boolean
  aan: boolean
  expliciet: boolean
}): '1' | '0' | null {
  if (!stand.klantfilterActief || !stand.expliciet) return null
  return stand.aan ? '1' : '0'
}

/** Draagt de URL een expliciete stand voor de schakelaar (`1` of `0`)? */
export function isToonNietIngeplandExpliciet(urlParam: string | null | undefined): boolean {
  return urlParam === '1' || urlParam === '0'
}

/** "2 monteurs · 3 diensten (1 onbemand)"; zonder diensten "geen inzet". */
export function formatKlantInzet(telling: PlanningBoardKlantInzetTelling): string {
  if (telling.diensten === 0) return 'geen inzet'
  const monteurs = `${telling.monteurs} ${telling.monteurs === 1 ? 'monteur' : 'monteurs'}`
  const diensten = `${telling.diensten} ${telling.diensten === 1 ? 'dienst' : 'diensten'}`
  const onbemand = telling.onbemand > 0 ? ` (${telling.onbemand} onbemand)` : ''
  return `${monteurs} · ${diensten}${onbemand}`
}

/**
 * De klant(en) uit het filter als leesbare naam voor kop, scheidingsregel en
 * drop-zone. Bij meer dan drie klanten wordt de staart samengevat, zodat een
 * breed filter de kop niet over meerdere regels trekt.
 */
export function klantweergaveKlantLabel(klanten: ReadonlyArray<{ naam: string }>): string {
  const namen = klanten.map((k) => k.naam.trim()).filter((n) => n.length > 0)
  if (namen.length === 0) return 'de gekozen klant'
  if (namen.length === 1) return namen[0]!
  if (namen.length <= 3) return `${namen.slice(0, -1).join(', ')} en ${namen[namen.length - 1]}`
  return `${namen.slice(0, 2).join(', ')} en ${namen.length - 2} andere klanten`
}

/**
 * De periode-aanduiding in twee vormen: `deze` voor de scheidingsregel ("Niet
 * ingepland bij X deze week") en `in` voor de lege kop ("Geen inzet bij X in
 * week 39"). `periodeLabel` is het label dat het bord al toont (dag of maand).
 */
export function klantweergavePeriodeTekst(
  view: PlanningView,
  context: { week?: number | null; periodeLabel: string },
): { deze: string; in: string } {
  if (view === 'dag') return { deze: 'deze dag', in: `op ${context.periodeLabel}` }
  if (view === 'maand') return { deze: 'deze maand', in: `in ${context.periodeLabel}` }
  return {
    deze: 'deze week',
    in: context.week ? `in week ${context.week}` : `in ${context.periodeLabel}`,
  }
}
